/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-anonymous-default-export */
import React, { useCallback, useRef, useState } from 'react'
import { Input, Button, Icon, FormPro } from '@roo/roo'
import Modal from '@roo/roo/Modal'
import PopConfirm from '@roo/roo/PopConfirm'
import { useTranslation } from '@sailor/i18n-web'
import { useRequest } from 'ahooks'
import {
  fetchExpenseCodeList,
  createExpenseCode,
  updateExpenseCode,
  deleteExpenseCode,
  IExpenseCodeItem,
} from './api'
import { successToast } from '@/common/utils/toast'
import CommonTable from '@/components/CommonTable'
import TipText from '@/components/TipText'
import styles from './index.module.less'

type ModalType = 'create' | 'edit'

interface ExpenseCodeModalState {
  visible: boolean;
  type: ModalType;
  record?: IExpenseCodeItem;
}

type CreateRow = {
  key: string;
  code: string;
  description: string;
  codeError?: string;
}

const ExpenseCodePage = () => {
  const { t } = useTranslation()
  const [inputVal, setInputVal] = useState('')
  const [query, setQuery] = useState({ keyword: '', pageNo: 1, pageSize: 10 })
  const { keyword, pageNo, pageSize } = query
  const [modal, setModal] = useState<ExpenseCodeModalState>({ visible: false, type: 'create' })
  const [form] = FormPro.useForm()
  const [createRows, setCreateRows] = useState<CreateRow[]>([{ key: `row-${Date.now()}`, code: '', description: '' }])

  const CODE_MAX_LENGTH = 64

  const handleRowCodeChange = useCallback((index: number, value: string) => {
    const lengthError = value.length > CODE_MAX_LENGTH ? t('e_pc_expense_code_length_limit', '费用代码超出长度限制（最多 64 个字符）') : undefined
    setCreateRows(prev => prev.map((row, i) => i === index ? { ...row, code: value, codeError: lengthError } : row))
  }, [t])

  const handleRowCodeBlur = useCallback(() => {
    setCreateRows(prev => {
      const codeCount: Record<string, number> = {}
      prev.forEach(row => {
        const code = row.code.trim()
        if (code) codeCount[code] = (codeCount[code] || 0) + 1
      })
      return prev.map(row => {
        const code = row.code.trim()
        const lenKey = t('e_pc_expense_code_length_limit', '费用代码超出长度限制（最多 64 个字符）')
        const dupKey = t('e_pc_expense_code_duplicate_hint', '存在重复费用代码，请调整')
        if (row.code.length > CODE_MAX_LENGTH) return { ...row, codeError: lenKey }
        if (code && codeCount[code] > 1) return { ...row, codeError: dupKey }
        if (row.codeError === dupKey) return { ...row, codeError: undefined }
        return row
      })
    })
  }, [t])

  const handleRowDescChange = useCallback((index: number, value: string) => {
    setCreateRows(prev => prev.map((row, i) => i === index ? { ...row, description: value } : row))
  }, [])

  const handleRowAdd = useCallback(() => {
    setCreateRows(prev => [...prev, { key: `row-${Date.now()}`, code: '', description: '' }])
  }, [])

  const handleRowRemove = useCallback((index: number) => {
    setCreateRows(prev => {
      const filtered = prev.filter((_, i) => i !== index)
      const codeCount: Record<string, number> = {}
      filtered.forEach(row => {
        const code = row.code.trim()
        if (code) codeCount[code] = (codeCount[code] || 0) + 1
      })
      const dupKey = t('e_pc_expense_code_duplicate_hint', '存在重复费用代码，请调整')
      return filtered.map(row => {
        const code = row.code.trim()
        if (code && codeCount[code] > 1) return { ...row, codeError: dupKey }
        if (row.codeError === dupKey) return { ...row, codeError: undefined }
        return row
      })
    })
  }, [t])

  const { data: listRes, loading, run: fetchList } = useRequest(
    () => fetchExpenseCodeList({ keyword: keyword || undefined, pageNo, pageSize }).then(res => res.data),
    { manual: true }
  )

  const listData = listRes?.result || []
  const totalCount = listRes?.totalCount || 0

  React.useEffect(() => {
    fetchList()
  }, [query])

  const handleSearch = () => {
    setQuery(prev => ({ ...prev, keyword: inputVal, pageNo: 1 }))
  }

  const refreshList = () => fetchList()

  const handleOpenCreate = () => {
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, code: '', description: '' }])
    setModal({ visible: true, type: 'create' })
  }

  const handleOpenEdit = (record: IExpenseCodeItem) => {
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, code: record.code, description: record.description || '' }])
    setModal({ visible: true, type: 'edit', record })
  }

  const handleModalClose = () => {
    setModal(prev => ({ ...prev, visible: false }))
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, code: '', description: '' }])
  }

  const { run: handleModalSubmit, loading: submitting } = useRequest(
    async () => {
      const { type, record } = modal
      if (type === 'edit' && record) {
        const row = createRows[0]
        if (!row.code.trim()) {
          setCreateRows(prev => prev.map((r, i) => i === 0 ? { ...r, codeError: t('e_pc_expense_code_search_placeholder', '请输入费用代码') } : r))
          return
        }
        if (row.code.length > CODE_MAX_LENGTH) {
          setCreateRows(prev => prev.map((r, i) => i === 0 ? { ...r, codeError: t('e_pc_expense_code_length_limit', '费用代码超出长度限制（最多 64 个字符）') } : r))
          return
        }
        await updateExpenseCode({
          id: record.id,
          code: row.code.trim(),
          description: row.description?.trim() || undefined,
        })
        successToast(t('e_pc_update_success', '修改成功'))
        handleModalClose()
        refreshList()
      } else {
        let hasError = false
        const validated = createRows.map(row => {
          if (!row.code.trim()) {
            hasError = true
            return { ...row, codeError: t('e_pc_expense_code_search_placeholder', '请输入费用代码') }
          }
          if (row.code.length > CODE_MAX_LENGTH) {
            hasError = true
            return { ...row, codeError: t('e_pc_expense_code_length_limit', '费用代码超出长度限制（最多 64 个字符）') }
          }
          return row
        })
        if (hasError) {
          setCreateRows(validated)
          return
        }
        const dupKey = t('e_pc_expense_code_duplicate_hint', '存在重复费用代码，请调整')
        const duplicateValidated = validated.map((row, i) => {
          const firstOccurrenceIndex = validated.findIndex(r => r.code.trim() === row.code.trim())
          return firstOccurrenceIndex !== i ? { ...row, codeError: dupKey } : row
        })
        if (duplicateValidated.some(row => row.codeError === dupKey)) {
          setCreateRows(duplicateValidated)
          return
        }
        // 批量创建
        const res = await createExpenseCode({
          expenseCodeCreateList: createRows.map(row => ({
            code: row.code.trim(),
            description: row.description?.trim() || undefined,
          })),
        })
        // 处理后端返回的重复冲突列表
        if (res?.conflictList && res.conflictList.length > 0) {
          const conflictCodes = new Set(res.conflictList.map(c => c.code))
          const dupKey = t('e_pc_expense_code_duplicate', '该费用代码已存在，请更换后重试')
          setCreateRows(prev => prev.map(row =>
            conflictCodes.has(row.code.trim()) ? { ...row, codeError: dupKey } : row
          ))
          return
        }
        successToast(t('e_pc_create_success', '创建成功'))
        handleModalClose()
        refreshList()
      }
    },
    { manual: true }
  )

  const { run: handleConfirmDelete, loading: deleting } = useRequest(
    async (record: IExpenseCodeItem) => {
      await deleteExpenseCode({ id: record.id })
      successToast(t('e_pc_del_success', '删除成功'))
      refreshList()
    },
    { manual: true }
  )

  const handlePageChange = (_pageNo: number, _pageSize: number) => {
    setQuery(prev => ({ ...prev, pageNo: _pageNo, pageSize: _pageSize }))
  }

  const isEdit = modal.type === 'edit'

  const columns = [
    {
      dataIndex: 'code',
      title: t('e_pc_expense_code_label', '费用代码'),
      render: (text: string) => <TipText text={text} />,
    },
    {
      dataIndex: 'description',
      title: t('e_pc_description', '描述'),
      render: (text: string) => <TipText text={text || '--'} />,
    },
    {
      dataIndex: '',
      title: t('batch_common_operation', '操作'),
      width: 160,
      fixed: 'right',
      render: (_: never, record: IExpenseCodeItem) => (
        <div>
          <Button type="brand-text" onClick={() => handleOpenEdit(record)}>
            {t('e_pc_edit', '编辑')}
          </Button>&ensp;&ensp;&ensp;
          <PopConfirm
            content={t('e_pc_confirm_delete_expense_code', '确认删除此费用代码？')}
            placement="left"
            onConfirm={() => handleConfirmDelete(record)}
            cancelText={t('e_pc_cancel', '取消')}
            confirmText={t('e_pc_submit', '确认')}
            disabled={deleting}
          >
            <Button
              type="brand-text"
              disabled={deleting}
            >
              {t('e_pc_del', '删除')}
            </Button>
          </PopConfirm>
        </div>
      ),
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <FormPro layout="vertical" size="small" labelCol={{ span: 12 }} wrapperCol={{ span: 12 }} style={{ paddingBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 12px', alignItems: 'end' }}>
          <FormPro.Item label={t('e_pc_expense_code_label', '费用代码')} style={{ marginBottom: 0 }}>
            <Input
              allowClear
              placeholder={t('e_pc_expense_code_search_placeholder', '请输入费用代码')}
              value={inputVal}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputVal(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') handleSearch()
              }}
            />
          </FormPro.Item>
          <div style={{ gridColumn: 'span 3', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <Button type="brand" size="normal" htmlType="button" onClick={handleSearch} className="btn-dark">
              {t('e_pc_aquire', '查询')}
            </Button>
          </div>
        </div>
      </FormPro>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="brand" size="normal" htmlType="button" onClick={handleOpenCreate}>
          {t('e_pc_expense_field_panel_title', '新增费用代码')}
        </Button>
      </div>

      <CommonTable
        rowKey="id"
        columns={columns}
        data={listData}
        loading={loading}
        pagination={{
          total: totalCount,
          currentPage: pageNo,
          pageSize,
          onChange: handlePageChange,
        }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={isEdit ? t('e_pc_expense_code_edit_popup', '编辑费用代码') : t('e_pc_expense_field_panel_title', '新增费用代码')}
        visible={modal.visible}
        onCancel={handleModalClose}
        showCancelButton={false}
        showConfirmButton={false}
        closeMotion={false}
        backdropClosable={false}
        style={{ width: '680px' }}
      >
        <FormPro
          form={form}
          colon={false}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 18 }}
          onFinish={handleModalSubmit}
        >
          <div className={styles.modalFormList}>
            {createRows.map((row, index) => (
              <div key={row.key} className={`${styles.formListItem} ${isEdit ? styles.formListItemNoIndex : ''}`}>
                {!isEdit && <span className={styles.formListIndex}>{index + 1}</span>}
                <div className={styles.formListFields}>
                  <div className={styles.formListFieldsRow}>
                    <div className={styles.formListCol}>
                      <div className={styles.formListColLabel}>
                        <span className={styles.formListColRequired}>*&nbsp;</span>
                        {t('e_pc_expense_code_label', '费用代码')}
                      </div>
                      <Input
                        value={row.code}
                        status={row.codeError ? 'error' : undefined}
                        placeholder={t('e_pc_expense_code_search_placeholder', '请输入费用代码')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRowCodeChange(index, e.target.value)}
                        onBlur={handleRowCodeBlur}
                        style={{ width: '100%' }}
                      />
                      {row.codeError && <div className={styles.formListColError}>{row.codeError}</div>}
                    </div>
                    <div className={styles.formListCol}>
                      <div className={styles.formListColLabel}>{t('e_pc_description', '描述')}</div>
                      <Input
                        value={row.description}
                        placeholder={t('e_pc_description_placeholder', '请输入描述')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRowDescChange(index, e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>
                {!isEdit && createRows.length > 1 && (
                  <Button type="brand-text" onClick={() => handleRowRemove(index)} className={styles.formListDeleteBtn}>
                    <Icon name="trash-o" size={20} />
                  </Button>
                )}
              </div>
            ))}
          </div>
          {!isEdit && (
            <div className={styles.addRowBtnWrap}>
              <Button className={styles.addRowBtn} type="brand-text" onClick={handleRowAdd}>
                + {t('e_pc_expense_field_panel_title', '新增费用代码')}
              </Button>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '20px', gap: '12px' }}>
            <Button type="hollow" onClick={handleModalClose}>
              {t('e_pc_cancel_button', '取消')}
            </Button>
            <Button type="brand" onClick={() => handleModalSubmit()} loading={submitting}>
              {t('e_pc_confirm_button', '确认')}
            </Button>
          </div>
        </FormPro>
      </Modal>
    </div>
  )
}

export default ExpenseCodePage
